
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '@/lib/api/api-client';
import { AccountType, OrganizationSubscription, SubscriptionStatus } from '@scrambl/shared';
import { useOrganizationsQuery } from '@/hooks/queries/useOrganizationsQuery';
import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/useOrganizationSubscription');

interface UseOrganizationSubscriptionReturn {
  subscription: OrganizationSubscription | null;
  isLoading: boolean;
  error: Error | null;
  hasActiveSubscription: boolean;
  needsSubscription: boolean;
  /** True when subscription status has been definitively determined (successful fetch or 404) */
  isFetched: boolean;
  refetch: () => Promise<void>;
  /** Sync subscription data from Stripe and update local cache */
  syncFromStripe: () => Promise<void>;
  /** Repair subscription data from Stripe (finds and imports active subscriptions) */
  repairFromStripe: () => Promise<OrganizationSubscription | null>;
}

// Cache subscriptions by organization ID to avoid repeated requests
const subscriptionCache = new Map<string, {
  subscription: OrganizationSubscription | null;
  timestamp: number;
}>();

// Track ongoing requests to prevent duplicate API calls
const ongoingRequests = new Map<string, Promise<void>>();

// Event listeners for cache invalidation
const cacheListeners = new Map<string, Set<() => void>>();

// Helper function to notify all listeners for an organization
const notifyListeners = (organizationId: string) => {
  const listeners = cacheListeners.get(organizationId);
  if (listeners) {
    listeners.forEach(listener => listener());
  }
};

// Global cache invalidation function
export const invalidateSubscriptionCache = (organizationId: string) => {
  subscriptionCache.delete(organizationId);
  ongoingRequests.delete(organizationId);
  notifyListeners(organizationId);
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface UseOrganizationSubscriptionOptions {
  enabled?: boolean;
}

export function useOrganizationSubscription(
  organizationId?: string, 
  options?: UseOrganizationSubscriptionOptions
): UseOrganizationSubscriptionReturn {
  const { enabled: enabledOption = true } = options || {};
  const [subscription, setSubscription] = useState<OrganizationSubscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isFetched, setIsFetched] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastLoggedState = useRef<string>('');
  // Which organization the state above describes. `organizationId` can change
  // under us (org switch, or the route guard correcting a primed org), and the
  // state keeps the previous org's answer until the new fetch resolves.
  const stateOrgIdRef = useRef<string | undefined>(undefined);

  // PERSONAL accounts have no subscription row — the subscription system is
  // inert for them (free by construction). Detect the target org's account type
  // from the already-cached organizations list (both OrgRouteGuard and
  // TodayRedirect derive their org id from this same query) so we can treat
  // personal accounts as "not applicable / allowed" rather than dead-on-arrival
  // needs-subscription redirects.
  const { data: organizations } = useOrganizationsQuery();
  const isPersonalOrg =
    !!organizationId &&
    (organizations ?? []).some(
      (org) => org.id === organizationId && org.accountType === AccountType.PERSONAL,
    );

  // Skip the subscription fetch entirely for personal accounts — there is no row
  // to fetch and no gate to enforce. Org accounts keep the caller's enabled flag.
  const enabled = enabledOption && !isPersonalOrg;

  const fetchSubscription = useCallback(async () => {
    // Don't fetch if disabled (e.g., auth not ready) or no org ID
    if (!enabled || !organizationId) {
      stateOrgIdRef.current = organizationId;
      setSubscription(null);
      setIsLoading(false);
      setIsFetched(false); // Not fetched - waiting for auth or org
      return;
    }

    // Check cache first
    const cached = subscriptionCache.get(organizationId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      stateOrgIdRef.current = organizationId;
      setSubscription(cached.subscription);
      setIsLoading(false);
      setIsFetched(true); // Fetched from cache
      setError(null);
      return;
    }

    // Check if there's already an ongoing request for this organization
    const ongoingRequest = ongoingRequests.get(organizationId);
    if (ongoingRequest) {
      // Wait for the ongoing request to complete
      await ongoingRequest;
      // After waiting, check cache again
      const updatedCache = subscriptionCache.get(organizationId);
      if (updatedCache) {
        stateOrgIdRef.current = organizationId;
        setSubscription(updatedCache.subscription);
        setIsLoading(false);
        setIsFetched(true); // Fetched from cache after waiting
        setError(null);
        return;
      }
    }

    // Create a new request
    const requestPromise = (async () => {
      try {
        setIsLoading(true);
        setError(null);
        setIsFetched(false);

        const response = await apiClient<{ data: OrganizationSubscription }>(
          `/subscriptions/organizations/${organizationId}`
        );

        const fetchedSubscription = response.data || null;

        // Log subscription data to help debug redirect issues
        logger.info(`[Subscription] Fetched for org ${organizationId}:`, {
          status: fetchedSubscription?.status,
          planKey: fetchedSubscription?.plan?.key,
          hasData: !!fetchedSubscription,
        });

        // Update cache
        subscriptionCache.set(organizationId, {
          subscription: fetchedSubscription,
          timestamp: Date.now(),
        });

        stateOrgIdRef.current = organizationId;
        setSubscription(fetchedSubscription);
        setIsFetched(true); // Successfully fetched

        // Notify all other hook instances using this organization
        notifyListeners(organizationId);
      } catch (err) {
        // If the API returns 404, it means no subscription exists - this is not an error
        if (err && typeof err === 'object' && 'status' in err && err.status === 404) {
          logger.info(`[Subscription] No subscription found (404) for org ${organizationId}`);
          stateOrgIdRef.current = organizationId;
          setSubscription(null);
          setError(null);
          setIsFetched(true); // 404 means we know there's no subscription - this is a definitive answer

          // Cache the "no subscription" state
          subscriptionCache.set(organizationId, {
            subscription: null,
            timestamp: Date.now(),
          });

          // Notify all other hook instances
          notifyListeners(organizationId);
        } else {
          logger.error('Failed to fetch subscription:', err);
          setError(err as Error);
          setIsFetched(false); // Error means we couldn't determine status - don't make decisions based on this
        }
      } finally {
        setIsLoading(false);
        // Remove from ongoing requests
        ongoingRequests.delete(organizationId);
      }
    })();

    // Store the request promise
    ongoingRequests.set(organizationId, requestPromise);
    
    // Wait for completion
    await requestPromise;
  }, [organizationId, refreshTrigger, enabled]);

  // Register listener for cache invalidation
  useEffect(() => {
    if (!organizationId) return;

    const listener = () => {
      setRefreshTrigger(prev => prev + 1);
    };

    // Add listener
    if (!cacheListeners.has(organizationId)) {
      cacheListeners.set(organizationId, new Set());
    }
    cacheListeners.get(organizationId)!.add(listener);

    // Cleanup
    return () => {
      const listeners = cacheListeners.get(organizationId);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          cacheListeners.delete(organizationId);
        }
      }
    };
  }, [organizationId]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const refetch = useCallback(async () => {
    // Clear cache for current organization and notify all listeners
    if (organizationId) {
      invalidateSubscriptionCache(organizationId);
    }
    await fetchSubscription();
  }, [organizationId, fetchSubscription]);

  const syncFromStripe = useCallback(async () => {
    if (!organizationId) return;

    try {
      setIsLoading(true);
      setError(null);

      // Call the sync endpoint which fetches latest from Stripe
      const response = await apiClient<{ data: OrganizationSubscription }>(
        `/subscriptions/organizations/${organizationId}/sync`,
        { method: 'POST' }
      );

      const syncedSubscription = response.data || null;

      // Update cache with synced data
      subscriptionCache.set(organizationId, {
        subscription: syncedSubscription,
        timestamp: Date.now(),
      });

      stateOrgIdRef.current = organizationId;
      setSubscription(syncedSubscription);
      setIsFetched(true);

      // Notify all other hook instances
      notifyListeners(organizationId);
    } catch (err) {
      logger.error('Failed to sync subscription from Stripe:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  const repairFromStripe = useCallback(async () => {
    if (!organizationId) return null;

    try {
      setIsLoading(true);
      setError(null);

      // Call the repair endpoint which can fix subscription mismatches
      const response = await apiClient<{
        success: boolean;
        message: string;
        data?: OrganizationSubscription;
      }>(
        `/subscriptions/organizations/${organizationId}/repair`,
        { method: 'POST' }
      );

      if (response.success && response.data) {
        // Update cache with repaired data
        subscriptionCache.set(organizationId, {
          subscription: response.data,
          timestamp: Date.now(),
        });

        stateOrgIdRef.current = organizationId;
        setSubscription(response.data);
        setIsFetched(true);

        // Notify all other hook instances
        notifyListeners(organizationId);
        return response.data;
      }

      return null;
    } catch (err) {
      logger.error('Failed to repair subscription from Stripe:', err);
      // Don't set error - repair failure is not critical
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  // Only report state that belongs to the organization being asked about.
  // Between an `organizationId` change and the new fetch resolving, the state
  // still holds the previous org's row; reporting that as fetched let callers
  // act on another org's subscription (OrgRouteGuard redirected an org with a
  // healthy subscription to subscription-setup because the previously loaded
  // org was past due).
  const isStateForRequestedOrg = stateOrgIdRef.current === organizationId;
  const effectiveSubscription = isStateForRequestedOrg ? subscription : null;
  const effectiveIsFetched = isStateForRequestedOrg && isFetched;
  const effectiveIsLoading =
    isLoading || (enabled && !!organizationId && !isStateForRequestedOrg);
  const effectiveError = isStateForRequestedOrg ? error : null;

  // Determine if organization has an active subscription
  const hasActiveSubscription = effectiveSubscription !== null && [
    SubscriptionStatus.ACTIVE,
    SubscriptionStatus.TRIALING
  ].includes(effectiveSubscription.status);

  // Determine if organization needs a subscription
  // Free plan organizations don't need a paid subscription.
  // PERSONAL accounts never need a subscription — the system is inert for them.
  const needsSubscription = isPersonalOrg
    ? false
    : !hasActiveSubscription &&
      (effectiveSubscription === null ||
        effectiveSubscription.plan?.key !== 'free');

  // Log subscription decision to help debug redirect issues (debug level to reduce noise)
  if (effectiveIsFetched && organizationId) {
    const currentState = `${effectiveSubscription?.status}-${hasActiveSubscription}-${needsSubscription}`;

    if (lastLoggedState.current !== currentState) {
      lastLoggedState.current = currentState;
      logger.debug(`[Subscription] Decision for org ${organizationId}:`, {
        status: effectiveSubscription?.status || 'null',
        planKey: effectiveSubscription?.plan?.key || 'null',
        hasActiveSubscription,
        needsSubscription,
        isFetched: effectiveIsFetched,
      });
    }
  }

  return {
    subscription: effectiveSubscription,
    isLoading: effectiveIsLoading,
    error: effectiveError,
    hasActiveSubscription,
    needsSubscription,
    isFetched: effectiveIsFetched,
    refetch,
    syncFromStripe,
    repairFromStripe,
  };
}